import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI, documentsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import {
  CheckCircle2,
  Circle,
  Upload,
  FileText,
  Loader2,
  AlertCircle,
  Send,
  LogOut,
} from 'lucide-react';

const DOC_LABELS = {
  passport: 'Passport',
  visa: 'Visa',
  work_permit: 'Work Permit',
  labor_contract: 'Labor Contract',
  emirates_id: 'Emirates ID',
  national_id: 'National ID',
  offer_letter: 'Offer Letter',
  bank_proof: 'Bank Proof',
  tax_document: 'Tax Document',
  educational_certificate: 'Educational Certificate',
  experience_certificate: 'Experience Certificate',
  medical_insurance: 'Medical Insurance',
  other: 'Other',
};

const COUNTRY_FIELDS = {
  UAE: ['emirates_id_number', 'emirates_id_expiry', 'visa_number', 'visa_expiry', 'labor_contract_number', 'labor_contract_expiry'],
  India: ['pan_number', 'aadhaar_number', 'uan_pf_number', 'bank_ifsc_code'],
  UK: ['national_insurance_number', 'right_to_work_status', 'brp_number', 'tax_code'],
  Singapore: ['nric_fin_number', 'cpf_number', 'work_pass_type', 'work_pass_expiry'],
  Ghana: ['ghana_card_number', 'ssnit_number', 'tin_number'],
  Kenya: ['national_id_number', 'kra_pin', 'nssf_number', 'nhif_number'],
  Uganda: ['national_id_number', 'tin_number', 'nssf_number'],
  Cameroon: ['national_id_number', 'tax_id_number', 'cnps_number'],
  Mauritius: ['national_id_number', 'tax_account_number', 'csg_number'],
};

const fieldLabel = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const MyOnboarding = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    personal_info: {},
    address_info: {},
    emergency_contact: {},
    bank_info: {},
    country_compliance: {},
    notes: '',
  });
  const [docCategory, setDocCategory] = useState('passport');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const fetchData = async () => {
    try {
      const res = await onboardingAPI.getMine();
      setData(res.data);
      if (res.data.onboarding) {
        setForm({
          personal_info: res.data.onboarding.personal_info || {},
          address_info: res.data.onboarding.address_info || {},
          emergency_contact: res.data.onboarding.emergency_contact || {},
          bank_info: res.data.onboarding.bank_info || {},
          country_compliance: res.data.onboarding.country_compliance || {},
          notes: res.data.onboarding.notes || '',
        });
      }
    } catch (e) {
      toast.error('Failed to load onboarding');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const updateField = (section, key, value) => {
    setForm(f => ({ ...f, [section]: { ...f[section], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onboardingAPI.updateMine(form);
      toast.success('Progress saved');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !data?.employee?.id) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', docCategory);
      await documentsAPI.upload(data.employee.id, fd);
      toast.success('Document uploaded');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onboardingAPI.submit();
      toast.success('Onboarding submitted for HR review');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (data && !data.onboarding_required) {
    // Active employee, redirect to dashboard
    navigate('/', { replace: true });
    return null;
  }

  const status = data?.status;
  const isReadOnly = status === 'pending_verification' || status === 'approved' || status === 'verified';
  const isApproved = status === 'approved' || status === 'active';
  const country = data?.country || 'UAE';
  const countryFields = COUNTRY_FIELDS[country] || [];
  const required = data?.required_documents || [];
  const uploaded = data?.uploaded_documents || [];
  const uploadedCategories = new Set(uploaded.map(d => d.category));
  const missing = required.filter(d => !uploadedCategories.has(d));
  const progress = required.length === 0 ? 100 : Math.round(((required.length - missing.length) / required.length) * 100);

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8" data-testid="onboarding-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Welcome to Pride Global</h1>
            <p className="text-muted-foreground mt-1">
              Complete your onboarding to unlock the full portal
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={isApproved ? 'default' : 'secondary'} data-testid="onboarding-status-badge">
              {(status || 'draft').replace(/_/g, ' ').toUpperCase()}
            </Badge>
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/login'); }}>
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Document Completion</span>
              <span className="text-sm font-bold text-primary" data-testid="onboarding-progress">{progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            {missing.length > 0 && (
              <div className="mt-3 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>Missing: {missing.map(m => DOC_LABELS[m] || m).join(', ')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {status === 'rejected' && data?.onboarding?.hr_notes && (
          <Card className="border-destructive">
            <CardContent className="p-4">
              <p className="font-semibold text-destructive">HR has rejected your onboarding:</p>
              <p className="text-sm mt-1">{data.onboarding.hr_notes}</p>
              <p className="text-xs mt-2 text-muted-foreground">Please correct the issues and resubmit.</p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="personal" className="space-y-4">
          <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full">
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="address">Address</TabsTrigger>
            <TabsTrigger value="emergency">Emergency</TabsTrigger>
            <TabsTrigger value="compliance">Country Compliance</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Tell us about yourself</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['full_name', 'date_of_birth', 'nationality', 'gender', 'marital_status', 'personal_email', 'mobile', 'alternate_mobile'].map(k => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{fieldLabel(k)}</Label>
                    <Input
                      type={k === 'date_of_birth' ? 'date' : 'text'}
                      value={form.personal_info[k] || ''}
                      onChange={(e) => updateField('personal_info', k, e.target.value)}
                      disabled={isReadOnly}
                      data-testid={`personal-${k}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="address">
            <Card>
              <CardHeader><CardTitle>Address Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['current_address', 'permanent_address', 'city', 'state', 'country', 'postal_code'].map(k => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{fieldLabel(k)}</Label>
                    <Input
                      value={form.address_info[k] || ''}
                      onChange={(e) => updateField('address_info', k, e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emergency">
            <Card>
              <CardHeader><CardTitle>Emergency Contact</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['name', 'relationship', 'phone', 'email'].map(k => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{fieldLabel(k)}</Label>
                    <Input
                      value={form.emergency_contact[k] || ''}
                      onChange={(e) => updateField('emergency_contact', k, e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="compliance">
            <Card>
              <CardHeader>
                <CardTitle>{country} Compliance Details</CardTitle>
                <CardDescription>Country-specific information for {country}</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {countryFields.map(k => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{fieldLabel(k)}</Label>
                    <Input
                      type={k.includes('expiry') ? 'date' : 'text'}
                      value={form.country_compliance[k] || ''}
                      onChange={(e) => updateField('country_compliance', k, e.target.value)}
                      disabled={isReadOnly}
                      data-testid={`compliance-${k}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Required Documents</CardTitle>
                <CardDescription>Upload the documents marked as required for {country}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-3">
                  {required.map(cat => {
                    const found = uploaded.find(d => d.category === cat);
                    return (
                      <div key={cat} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`doc-row-${cat}`}>
                        <div className="flex items-center gap-3">
                          {found ? <CheckCircle2 className="text-emerald-500" size={20} /> : <Circle className="text-muted-foreground" size={20} />}
                          <div>
                            <p className="font-medium">{DOC_LABELS[cat] || cat}</p>
                            {found && <p className="text-xs text-muted-foreground">{found.file_name} · {found.status}</p>}
                          </div>
                        </div>
                        {!found && !isReadOnly && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => { setDocCategory(cat); handleDocUpload(e); }}
                            />
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm hover:bg-muted">
                              <Upload size={14} /> Upload
                            </span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Optional extra docs */}
                {!isReadOnly && (
                  <div className="pt-2 border-t">
                    <Label className="text-xs">Upload additional document</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        value={docCategory}
                        onChange={(e) => setDocCategory(e.target.value)}
                        className="border rounded-md px-2 text-sm bg-background"
                      >
                        {Object.entries(DOC_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <label className="cursor-pointer flex-1">
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocUpload} data-testid="upload-additional-doc" />
                        <Button asChild variant="outline" size="sm" disabled={uploadingDoc}>
                          <span>{uploadingDoc ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />} Upload</span>
                        </Button>
                      </label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex flex-col sm:flex-row gap-3 justify-end sticky bottom-4">
            <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-onboarding-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || missing.length > 0} data-testid="submit-onboarding-btn">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send size={16} className="mr-2" />
              Submit for HR Verification
            </Button>
          </div>
        )}

        {isApproved && (
          <Card className="border-emerald-500">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500 mb-2" />
              <p className="font-bold text-lg">Onboarding Approved!</p>
              <p className="text-sm text-muted-foreground mb-4">You can now access the full portal.</p>
              <Button onClick={() => navigate('/', { replace: true })} data-testid="enter-portal-btn">Enter Portal</Button>
            </CardContent>
          </Card>
        )}

        {status === 'pending_verification' && (
          <Card>
            <CardContent className="p-6 text-center">
              <FileText className="mx-auto h-12 w-12 text-primary mb-2" />
              <p className="font-bold text-lg">Pending HR Verification</p>
              <p className="text-sm text-muted-foreground">Your documents are being reviewed. You will receive a notification once approved.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyOnboarding;
